/**
 * WaitTimePanel — student-reported consulate appointment wait times on
 * the country dossier. Fetches aggregates from
 * `/api/wait-times/country/{country}`. Renders NOTHING when fewer than
 * MIN_REPORTS_FOR_DISPLAY reports exist for any consulate (same honesty
 * pattern as CohortHighlights).
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Clock3, UsersRound } from "lucide-react";

function _fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return ""; }
}

export default function WaitTimePanel({ country }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!country) return;
    api.get(`/wait-times/country/${encodeURIComponent(country)}`)
      .then(r => setData(r.data))
      .catch(() => setData({ consulates: [] }));
  }, [country]);

  if (!data || !data.consulates?.length) return null;

  return (
    <div
      data-testid="wait-time-panel"
      className="mt-6 moe-card p-6"
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-elec font-bold">
        <Clock3 size={12} strokeWidth={2.4} />
        Student-reported appointment wait
      </div>
      <div className="mt-1 text-[12px] text-muted leading-snug">
        Days from first booking attempt to actual appointment. Reported by real
        Ministry of Exchange students. Not an official figure.
      </div>
      <div className="mt-4 grid md:grid-cols-2 gap-3">
        {data.consulates.map((c) => (
          <div
            key={c.city}
            data-testid={`wait-time-consulate-${c.city.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="border border-ink/12 rounded-lg p-4"
          >
            <div className="text-sm font-bold text-ink">{c.city}</div>
            <div className="mt-1 flex items-baseline gap-2">
              <div className="text-2xl font-bold tracking-tight text-ink">{c.avg_days}</div>
              <div className="text-[11px] text-muted">days on average</div>
            </div>
            <div className="mt-1 text-[11px] text-muted">
              Range: {c.min_days}–{c.max_days} days
            </div>
            <div className="mt-2 pt-2 border-t border-ink/8 flex items-center gap-1.5 text-[11px] text-muted">
              <UsersRound size={11} strokeWidth={2.4} />
              Based on <span className="font-semibold text-ink">{c.count} student reports</span>
              {c.latest_report_date && (
                <span> · latest {_fmtDate(c.latest_report_date)}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
